/**
 * Expands an IPv6 address by replacing shorthand notations (::) with the full expanded form. It has
 * no effect on IPv4 addresses. Useful for if you need to generate a consistent hash output from
 * various representations of an IP address.
 *
 * This function takes a shortened IPv6 address (e.g., `2001:0db8::1`) and expands it to its full
 * form (e.g., `2001:0db8:0000:0000:0000:0000:0000:0001`). Alternative shorthand:
 * `2001:0DB8:0:0:0:0:0:1` will output the same value.
 *
 * - Replaces shorthand :: with contiguous block of zero sections (e.g. "::" can be replaced with
 *   "0000:0000:0000:0000")
 * - Ensures that each segment has 4 characters by padding with leading zeros (e.g., "1" becomes
 *   "0001").
 * - Replaces uppercase letters with lower case equivalents
 *
 * @param input - The shortened IPv6 address to expand.
 * @returns The expanded IPv6 address.
 */
export function standardizeIpAddress(input: string): string {
  if (ipv4Pattern.test(input)) return input;

  // IPV6 addresses are case insensitive, so always return lower case for consistency
  if (isShortIpv6Pattern.test(input)) {
    const sections = input.split(':');

    // ensure each section has 4 characters
    const expanded = sections.map(s => s.padStart(4, '0'));

    return expanded.join(':').toLowerCase();
  }

  // input contains IPV6 shorthand, so expand it to full form.
  const shorthandSegments = input.split('::');
  if (shorthandSegments.length === 2) {
    const left = shorthandSegments[0]!.split(':');
    const right = shorthandSegments[1]!.split(':');
    const missingSections = 8 - (left.length + right.length);

    // Re-add omitted sections and ensure each has 4 characters in each
    const expanded = [...left, ...Array(missingSections).fill('0000'), ...right].map(section =>
      section.padStart(4, '0')
    );

    return expanded.join(':').toLowerCase();
  }

  // otherwise return unmodified input
  return input;
}

const ipv4Pattern = /^((25[0-5]|(2[0-4]|1\d|[1-9]|)\d)\.?\b){4}$/;
const isShortIpv6Pattern = /^(?:[0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$/;
